/**
 * DPoP Debug Logger
 *
 * Centralises all DPoP / TokenManager diagnostic output behind a single flag so
 * that operational details (token state, key-pair lifecycle, endpoint URLs, expiry
 * timestamps) are never visible in a production browser DevTools console.
 *
 * ## Default behaviour
 *   - Development builds  (Vite `import.meta.env.DEV === true`): logging ON
 *   - Production builds   (`import.meta.env.DEV === false`):      logging OFF
 *
 * ## Runtime override (useful for debugging a production deployment)
 *   Enable:  localStorage.setItem('dpop_debug', 'true')  then reload
 *   Disable: localStorage.setItem('dpop_debug', 'false') then reload
 *   Reset to build default: localStorage.removeItem('dpop_debug') then reload
 *
 * NOTE: console.error calls are intentionally NOT routed through this logger —
 * errors must always surface regardless of the flag.
 */

function isDPoPDebugEnabled(): boolean {
  try {
    // localStorage key takes precedence over the build-time default, allowing
    // a developer to toggle logging in a deployed environment without a rebuild.
    const override = localStorage.getItem('dpop_debug');
    if (override !== null) return override === 'true';
  } catch {
    // localStorage unavailable (e.g. SSR, sandboxed iframe) — fall through to build default
  }
  // Default: on in Vite dev server, off in production build.
  return (import.meta as any).env?.DEV === true;
}

/**
 * Debug-gated console.log for DPoP / TokenManager operational messages.
 */
export function dpopLog(...args: unknown[]): void {
  if (isDPoPDebugEnabled()) {
    console.log(...args);
  }
}

/**
 * Debug-gated console.warn for DPoP / TokenManager operational warnings.
 */
export function dpopWarn(...args: unknown[]): void {
  if (isDPoPDebugEnabled()) {
    console.warn(...args);
  }
}
